import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  Input,
  Stack,
  Text,
  VStack,
  useColorModeValue,
  Icon,
  Flex,
  Alert,
  AlertIcon,
  AlertDescription,
  Spinner,
  Badge,
  Divider
} from '@chakra-ui/react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { FiUser, FiSearch, FiRefreshCw, FiUserCheck, FiCreditCard, FiHash } from 'react-icons/fi';
import PartnerDataBox from '../components/partner-data-box';
import RadioButtons from '../components/radio-group';
import { Member } from '../models/member';
import { isMobile } from 'react-device-detect';
import { findMember } from '../services/member-service';
import ColorModeToggle from '../components/color-mode-toggle';

const PENDING = 'Pending';
const IN_PROGRESS = 'In Progress';
const COMPLETE = 'Complete';
const NOT_FOUND = 'Not Found';
const FOUND = 'Found';

function FirstLogin(): JSX.Element {
  const [displayMembershipNumberSearch, setDisplayMembershipNumberSearch] = useState(true);
  const [displayDocumentNumberSearch, setDisplayDocumentNumberSearch] = useState(false);
  const [partnerData, setPartnerData] = useState(null as unknown as Member);
  const [queryStatus, setQueryStatus] = useState(PENDING);
  const [queryResult, setQueryResult] = useState('');


  const bgGradient = useColorModeValue(
    'linear(135deg, gray.50 0%, purple.50 100%)',
    'linear(135deg, gray.900 0%, gray.800 100%)'
  );
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const headingColor = useColorModeValue('purple.700', 'purple.300');
  const successCardBorder = useColorModeValue('green.100', 'gray.700');
  const successCardHeaderBg = useColorModeValue('green.500', 'green.600');

  const formik = useFormik({
    initialValues: {
      memberNumber: '',
      documentNumber: ''
    },
    validationSchema: Yup.object({
      memberNumber: Yup.number()
        .positive('El número de socio ingresado no es válido.')
        .integer('El número de socio ingresado no es válido.'),
      documentNumber: Yup.number()
        .positive('El número de documento ingresado no es válido.')
        .integer('El número de documento ingresado no es válido.')
    }),
    onSubmit: async (values) => {
      if (displayMembershipNumberSearch && values.memberNumber == null) {
        return;
      }
      if (displayDocumentNumberSearch && values.documentNumber == null) {
        return;
      }

      setQueryStatus(IN_PROGRESS);
      const dataNumber = displayMembershipNumberSearch
        ? values.memberNumber
        : values.documentNumber;
      const paramName = displayMembershipNumberSearch
        ? 'memberNumber'
        : 'documentNumber';

      const response = await findMember({ parameterName: paramName, parameterData: dataNumber });

      setQueryStatus(COMPLETE);
      switch (response.statusCode) {
        case 200:
          setQueryResult(FOUND);
          if (response.response !== null) {
            setPartnerData(response.response);
          }
          break;
        case 204:
          setQueryResult(NOT_FOUND);
          break;
        default:
          setQueryResult(NOT_FOUND);
          break;
      }
    }
  });

  const handleRadioButtonChange = (selectedValue: string): void => {
    switch (selectedValue) {
      case '1':
        setDisplayDocumentNumberSearch(false);
        setDisplayMembershipNumberSearch(true);
        break;
      case '2':
        setDisplayDocumentNumberSearch(true);
        setDisplayMembershipNumberSearch(false);
        break;
    }
  };

  const retrySearch = (): void => {
    setQueryStatus(PENDING);
    setQueryResult('');
    formik.resetForm();
  };

  const SearchButtons = (): JSX.Element => {
    const isSearchDisabled = queryStatus === COMPLETE || 
      ((displayMembershipNumberSearch && !formik.values.memberNumber) || 
       (displayDocumentNumberSearch && !formik.values.documentNumber));

    return (
      <Stack
        direction={isMobile ? 'column' : 'row'}
        spacing={4}
        w="full"
        align="center"
      >
        <Button
          type="submit"
          colorScheme="purple"
          size="lg"
          isDisabled={isSearchDisabled}
          isLoading={queryStatus === IN_PROGRESS}
          loadingText="Buscando..."
          leftIcon={<FiSearch />}
          flex={1}
          _hover={{
            transform: 'translateY(-2px)',
            boxShadow: 'lg'
          }}
        >
          Buscar socio
        </Button>
        <Button
          type="button"
          variant="outline"
          colorScheme="purple"
          size="lg"
          isDisabled={queryStatus !== COMPLETE}
          onClick={retrySearch}
          leftIcon={<FiRefreshCw />}
          flex={1}
          _hover={{
            transform: 'translateY(-2px)',
            boxShadow: 'lg'
          }}
        >
          Nueva búsqueda
        </Button>
      </Stack>
    );
  };

  return (
    <Box
      minH="100vh"
      bgGradient={bgGradient}
      py={8}
      className="fade-in-up"
    >
      <Container maxW="4xl" centerContent>
        <Flex
          direction="column"
          align="center"
          w="full"
          mb={8}
        >
          <HStack justify="space-between" w="full" mb={4}>
            <Box />
            <ColorModeToggle />
          </HStack>
          
          <Card
            w="full"
            maxW="2xl"
            shadow="2xl"
            bg={cardBg}
            borderRadius="2xl"
            border="1px"
            borderColor={useColorModeValue('purple.100', 'gray.700')}
            className="card-hover"
            overflow="hidden"
          >
            <CardHeader
              bg={useColorModeValue('purple.500', 'purple.600')}
              color="white"
              textAlign="center"
              py={6}
            >
              <VStack spacing={2}>
                <Icon as={FiUserCheck} boxSize={8} />
                <Heading size="lg" fontWeight="bold">
                  ¿Ya sos socio?
                </Heading>
                <Text fontSize="sm" opacity={0.9}>
                  Vinculá tu cuenta con tu número de socio
                </Text>
              </VStack>
            </CardHeader>

            <CardBody p={8}>
              <form onSubmit={formik.handleSubmit}>
                <Stack spacing={8}>
                  <VStack spacing={4} align="stretch">
                    <Text color={textColor} textAlign="center" fontSize="md">
                      Para vincular tu cuenta, necesitamos tu número de socio o documento de identidad
                    </Text>
                    
                    <Box>
                      <RadioButtons
                        callback={handleRadioButtonChange}
                        isDisabled={queryStatus === COMPLETE}
                      />
                    </Box>
                  </VStack>

                  <VStack spacing={6}>
                    {displayMembershipNumberSearch && (
                      <FormControl
                        isInvalid={
                          (formik.touched.memberNumber ?? false) &&
                          formik.errors.memberNumber != null
                        }
                      >
                        <FormLabel
                          htmlFor="member-number"
                          color={textColor}
                          fontWeight="semibold"
                          display="flex"
                          alignItems="center"
                          gap={2}
                        >
                          <Icon as={FiHash} />
                          Número de socio
                        </FormLabel>
                        <Input
                          id="member-number"
                          name="memberNumber"
                          type="number"
                          value={formik.values.memberNumber}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          isDisabled={queryStatus === COMPLETE}
                          placeholder="Ingresá tu número de socio"
                          size="lg"
                          borderRadius="lg"
                          focusBorderColor="purple.500"
                          _hover={{
                            borderColor: 'purple.300'
                          }}
                        />
                        <FormErrorMessage>
                          {formik.errors.memberNumber}
                        </FormErrorMessage>
                      </FormControl>
                    )}

                    {displayDocumentNumberSearch && (
                      <FormControl
                        isInvalid={
                          (formik.touched.documentNumber ?? false) &&
                          formik.errors.documentNumber != null
                        }
                      >
                        <FormLabel
                          htmlFor="document-number"
                          color={textColor}
                          fontWeight="semibold"
                          display="flex"
                          alignItems="center"
                          gap={2}
                        >
                                                     <Icon as={FiCreditCard} />
                           Número de documento
                        </FormLabel>
                        <Input
                          id="document-number"
                          name="documentNumber"
                          type="number"
                          value={formik.values.documentNumber}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          isDisabled={queryStatus === COMPLETE}
                          placeholder="Ingresá tu número de documento"
                          size="lg"
                          borderRadius="lg"
                          focusBorderColor="purple.500"
                          _hover={{
                            borderColor: 'purple.300'
                          }}
                        />
                        <FormErrorMessage>
                          {formik.errors.documentNumber}
                        </FormErrorMessage>
                      </FormControl>
                    )}

                    {queryStatus === COMPLETE && queryResult === NOT_FOUND && (
                      <Alert status="warning" borderRadius="lg">
                        <AlertIcon />
                        <AlertDescription>
                          No encontramos ningún socio con los datos ingresados. 
                          Por favor, verificá la información e intentá nuevamente.
                        </AlertDescription>
                      </Alert>
                    )}

                    {queryStatus === IN_PROGRESS && (
                      <Alert status="info" borderRadius="lg">
                        <Spinner size="sm" mr={2} />
                        <AlertDescription>
                          Buscando información del socio...
                        </AlertDescription>
                      </Alert>
                    )}
                  </VStack>

                  <SearchButtons />
                </Stack>
              </form>
            </CardBody>
          </Card>

          {queryStatus === COMPLETE && queryResult === FOUND && partnerData && (
            <Card
              w="full"
              maxW="2xl"
              mt={8}
              shadow="2xl"
              bg={cardBg}
              borderRadius="2xl"
              border="1px"
              borderColor={successCardBorder}
              className="card-hover"
            >
              <CardHeader
                bg={successCardHeaderBg}
                color="white"
                textAlign="center"
                py={4}
              >
                <HStack justify="center" spacing={2}>
                  <Icon as={FiUserCheck} boxSize={6} />
                  <Heading size="md" fontWeight="bold">
                    ¡Socio encontrado!
                  </Heading>
                </HStack>
              </CardHeader>
              <CardBody p={8}>
                <PartnerDataBox member={partnerData} />
              </CardBody>
            </Card>
          )}
        </Flex>
      </Container>
    </Box>
  );
}

export default FirstLogin;
